define('presentation/settings/callQueues/viewModels/addCallQueueViewModel',[
    'businessServices/router/router',
    'businessServices/state/modelStateObserver',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'presentation/common/routeToModal/strategies/callQueuePoolStrategy',
    'presentation/common/routeToModal/viewModels/routeToViewModel',
    'presentation/settings/callQueues/facades/addCallQueueFacade',
    'presentation/settings/callQueues/validators/addCallQueueViewModelValidator',
    'presentation/settings/callQueues/viewModels/callQueuePoolViewModel',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const CallQueuePoolViewModelConstructor = require('presentation/settings/callQueues/viewModels/callQueuePoolViewModel');
        const ModelStateObserverConstructor = require('businessServices/state/modelStateObserver');
        const RouteToStrategyConstructor = require('presentation/common/routeToModal/strategies/callQueuePoolStrategy');
        const RouteToViewModelConstructor = require('presentation/common/routeToModal/viewModels/routeToViewModel');
        const SorterConstructor = require('common/collections/collectionSorter');

        const _commonState = require('common/storage/commonState');
        const _i18n = require('i18next');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');

        const NO_GREETING_ID = "noGreeting";

        // Same please-select starting point the pools use.
        const NO_DESTINATION_ROUTE_TO_TYPE = 'autoAttendant';
        const NO_DESTINATION_ROUTE_TO_DATA = {autoAttendantId: "[None]"};

        let _facade = null;
        let _validator = null;

        self.accountCallQueueId = null;
        self.callQueuesUrl = _navigationConfiguration.routesById.callQueues.url;
        self.isCompositionComplete = ko.observable(false);
        self.isNewCallQueue = ko.observable(false);

        // The route-to editor reads this off its parent; a call queue has no callflow of its own to
        // invalidate, so it is always satisfied here.
        self.isCallflowValid = ko.observable(true);
        self.canDisplayValidation = ko.observable(false);

        self.name = ko.observable('').extend({observeState: true});
        self.selectedGreetingId = ko.observable(NO_GREETING_ID).extend({observeState: true});
        self.pools = ko.observableArray([]).extend({observeState: true});
        self.queueFullRoutingRuleViewModel = ko.observable().extend({observeState: true});
        self.routeToStrategy = new RouteToStrategyConstructor();

        self.modelStateObserver = new ModelStateObserverConstructor(self, true);

        self.existingCallQueueNames = ko.observableArray([]);

        self.headerI18nKey = ko.pureComputed(() => self.accountCallQueueId ? "addCallQueue:headerEdit" : "addCallQueue:headerAdd");
        self.saveButtonTitle = ko.pureComputed(() => self.accountCallQueueId ? _i18n.t('addCallQueue:save') : _i18n.t('addCallQueue:add'));

        self.availableGreetings = ko.pureComputed(() => {
            const prompts = _commonState.prompts().map((prompt) => {
                return {
                    id: prompt.id,
                    text: ko.unwrap(prompt.name),
                    isSystemPrompt: prompt.isSystemPrompt === true
                };
            });

            const sorter = new SorterConstructor();
            sorter.sort(prompts, "text", true);

            return [{id: NO_GREETING_ID, text: _i18n.t('addCallQueue:noGreeting'), isSystemPrompt: false}].concat(prompts);
        });

        self.isQueueFullRoutingRuleValid = ko.observable(true);

        self.poolValidationError = ko.observable(false);
        self.invalidPoolNames = ko.observableArray([]);
        self.warningMessages = ko.pureComputed(() => {
            return [
                {
                    message: _i18n.t('addCallQueue:validationPoolDescription'),
                    listItems: self.invalidPoolNames()
                },
                {
                    message: _i18n.t('addCallQueue:validationPleaseFix'),
                    listItems: []
                }
            ];
        });

        self.canDeletePool = ko.computed(() => self.pools().length > 1);

        self.addAnotherPool = () => {
            const pool = new CallQueuePoolViewModelConstructor(self, null);
            pool.sequence(self.pools().length + 1);
            self.pools.push(pool);
            self.modelStateObserver.addChildObserver(pool.modelStateObserver);
        };

        self.deletePool = (pool) => {
            if (self.canDeletePool() === false) {
                return;
            }

            self.pools.remove(pool);
            // Priority is the position in the list, so closing the gap keeps the fill order the
            // same as the order shown.
            self.pools().forEach((remainingPool, index) => remainingPool.sequence(index + 1));
        };

        self.shouldScrollTop = ko.observable(false);

        const _validateRouteToViewModelCallback = () => {
            self.canDisplayValidation(false);
            return true;
        };

        // Where callers go when no pool has room for them. Belongs to the queue rather than to the
        // callflow that routes into it, so one queue answers the same way wherever it is used.
        const _buildQueueFullRoutingRuleViewModel = (routeToType, routeToData) => {
            const routeToViewModel = new RouteToViewModelConstructor(routeToType, routeToData, _validateRouteToViewModelCallback, {
                subMenu: {enabled: false}
            });
            routeToViewModel.routeToStrategy = self.routeToStrategy;
            routeToViewModel.showAnswerTimeoutOptions(false);
            routeToViewModel.routeTitle(_i18n.t('addCallQueue:queueFullRouteToTitle'));
            routeToViewModel.showRouteToTitle(true);
            routeToViewModel.showRouteToOptions(true);
            routeToViewModel.parentViewModel(self);
            routeToViewModel.modelStateObserver.commitData();

            return routeToViewModel;
        };

        self.validate = () => _validator.validate();

        self.save = () => {
            return _promiseFactory.deferWithMinimumWait((promise) => {
                _isValidToSave()
                    .fail(promise.reject)
                    .done((isValid) => {
                        self.shouldScrollTop(!isValid);
                        if (isValid === false) {
                            promise.resolve();
                            self.shouldScrollTop(false);
                            return;
                        }

                        const callQueue = _buildCallQueue();

                        const facadePromise = self.accountCallQueueId === null ?
                            _facade.create(callQueue) :
                            _facade.update(callQueue);

                        facadePromise
                            .fail(promise.reject)
                            .done(() => {
                                self.modelStateObserver.saveData();
                                _router.navigate(self.callQueuesUrl);
                                promise.resolve();
                            });
                    });
            });
        };

        self.cancel = () => {
            self.modelStateObserver.navigateWithoutWarning = true;
            _router.navigate(self.callQueuesUrl);
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
            self.modelStateObserver.commitData();
            if (self.accountCallQueueId === null) {
                self.isNewCallQueue(true);
            }
        };

        // Both greeting ids are sent, because the two are separate columns on the queue: a system
        // clip and a customer recording are not interchangeable ids.
        const _buildCallQueue = () => {
            const selectedGreetingId = self.selectedGreetingId();
            const selectedGreeting = self.availableGreetings().find((greeting) => greeting.id === selectedGreetingId);
            const isSystemGreeting = selectedGreeting !== undefined && selectedGreeting.isSystemPrompt;
            const hasGreeting = selectedGreeting !== undefined && selectedGreeting.id !== NO_GREETING_ID;

            return {
                accountCallQueueId: self.accountCallQueueId,
                name: self.name().trim(),
                welcomeVoicePromptId: hasGreeting && isSystemGreeting === false ? selectedGreeting.id : null,
                welcomeSystemAudioClipId: hasGreeting && isSystemGreeting ? selectedGreeting.id : null,
                queueFullRoutingRule: self.routeToStrategy.buildRoutingRule(self.queueFullRoutingRuleViewModel()),
                pools: self.pools().map((pool) => pool.getPool())
            };
        };

        const _isValidToSave = () => {
            return _promiseFactory.defer((promise) => {
                _validator.validate()
                    .fail(promise.reject)
                    .done((isValid) => {
                        self.invalidPoolNames.removeAll();
                        const poolsValidatorPromiseFactory = new PromiseFactoryConstructor();
                        let areAllPoolsValid = isValid;

                        self.pools().forEach((pool) => {
                            poolsValidatorPromiseFactory.defer((poolsValidatorPromise) => {
                                pool.validator.validate()
                                    .fail(poolsValidatorPromise.reject)
                                    .done((isPoolValid) => {
                                        if (isPoolValid === false) {
                                            self.invalidPoolNames.push(pool.legendPoolName());
                                        }

                                        self.canDisplayValidation(true);
                                        pool.canDisplayValidation(true);
                                        areAllPoolsValid = areAllPoolsValid && isPoolValid;
                                        poolsValidatorPromise.resolve();
                                    });
                            });
                        });

                        poolsValidatorPromiseFactory.wait()
                            .fail(promise.reject)
                            .done(() => {
                                self.poolValidationError(areAllPoolsValid === false);
                                promise.resolve(areAllPoolsValid);
                            });
                    });
            });
        };

        const _populateNewCallQueueForm = () => {
            return _promiseFactory.defer((promise) => {
                self.addAnotherPool();
                self.queueFullRoutingRuleViewModel(_buildQueueFullRoutingRuleViewModel(NO_DESTINATION_ROUTE_TO_TYPE, NO_DESTINATION_ROUTE_TO_DATA));
                self.modelStateObserver.addChildObserver(self.queueFullRoutingRuleViewModel().modelStateObserver);
                promise.resolve();
            });
        };

        const _populateExistingCallQueueForm = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getCallQueue(self.accountCallQueueId)
                    .fail(promise.reject)
                    .done((callQueue) => {
                        self.name(callQueue.name);

                        if (callQueue.welcomeVoicePromptId !== null) {
                            self.selectedGreetingId(callQueue.welcomeVoicePromptId);
                        } else if (callQueue.welcomeSystemAudioClipId !== null) {
                            self.selectedGreetingId(callQueue.welcomeSystemAudioClipId);
                        }

                        // A queue saved before it had a queue-full destination has none to load, so
                        // it opens on please-select rather than on whatever the first option is.
                        const queueFullRoutingRuleViewModel = callQueue.queueFullRoutingRule ?
                            _buildQueueFullRoutingRuleViewModel('routingRule', callQueue.queueFullRoutingRule) :
                            _buildQueueFullRoutingRuleViewModel(NO_DESTINATION_ROUTE_TO_TYPE, NO_DESTINATION_ROUTE_TO_DATA);
                        self.queueFullRoutingRuleViewModel(queueFullRoutingRuleViewModel);
                        self.modelStateObserver.addChildObserver(queueFullRoutingRuleViewModel.modelStateObserver);

                        callQueue.pools
                            .slice()
                            .sort((firstPool, secondPool) => firstPool.priority - secondPool.priority)
                            .forEach((originalPool, index) => {
                                const pool = new CallQueuePoolViewModelConstructor(self, originalPool);
                                pool.sequence(index + 1);
                                self.pools.push(pool);
                                self.modelStateObserver.addChildObserver(pool.modelStateObserver);
                            });

                        promise.resolve();
                    });
            });
        };

        const _populateExistingCallQueueNames = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getCallQueues()
                    .fail(promise.reject)
                    .done((callQueues) => {
                        self.existingCallQueueNames(callQueues.map((callQueue) => {
                            return {
                                accountCallQueueId: callQueue.accountCallQueueId,
                                name: callQueue.name
                            };
                        }));
                        promise.resolve();
                    });
            });
        };

        self.activate = (callQueueId) => {
            if (callQueueId) {
                self.accountCallQueueId = callQueueId;
            }

            const FacadeConstructor = require('presentation/settings/callQueues/facades/addCallQueueFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/settings/callQueues/validators/addCallQueueViewModelValidator');
            _validator = new ValidatorConstructor();

            return _initialize();
        };

        const _initialize = () => {
            _promiseFactory.defer((allDonePromise) => {
                _populateExistingCallQueueNames()
                    .fail(allDonePromise.reject)
                    .done(() => {
                        const populatePromise = self.accountCallQueueId === null ?
                            _populateNewCallQueueForm() :
                            _populateExistingCallQueueForm();

                        populatePromise
                            .fail(allDonePromise.reject)
                            .done(() => {
                                _validator.registerViewModel(self);
                                allDonePromise.resolve();
                            });
                    });
            });

            return _promiseFactory.wait();
        };
    };
});

